import { Button } from '@mui/material';
import {
  IntegrationPropertiesFormSchema,
  PropertyMap,
  Provider,
  ProviderMap,
} from '@strength/domain';
import { I18nTranslator } from '@strength/i18n';
import { MdClose } from '@teamworksdev/icons';
import { IntegrationProviderOptions } from '../../../pages/library/exercises/types';
import {
  Cell,
  CellEditBehavior,
  Region,
  Slot,
  SlotRenderContentProps,
} from '../types';
import { relativeCell } from '../utils';
import { CellInputWithDropdown } from './common';

const COLUMN_KEYS = {
  PROVIDER: 0,
  PROPERTY: 1,
  VALUE: 2,
} as const;

const HEADER_NAMES = (t: I18nTranslator) =>
  ({
    [COLUMN_KEYS.PROVIDER]: t('provider'),
    [COLUMN_KEYS.PROPERTY]: t('property'),
    [COLUMN_KEYS.VALUE]: t('value'),
  }) as const;

const KEY_NAMES = {
  [COLUMN_KEYS.PROVIDER]: 'provider',
  [COLUMN_KEYS.PROPERTY]: 'property',
  [COLUMN_KEYS.VALUE]: 'value',
} as const;

export class IntegrationProviderSlot implements Slot<undefined> {
  public readonly region: Region;
  public readonly disabled: boolean;
  public readonly gridSubRowSpan: number;

  constructor(
    private readonly anchorCell: Cell,
    private readonly providerProperties: IntegrationPropertiesFormSchema,
    private readonly onChange: (
      providerProperties: IntegrationPropertiesFormSchema,
    ) => void,
    private readonly integrationProviderOptions: IntegrationProviderOptions[],
    disabled: boolean,
    gridSubRowSpan: number,
    private readonly t: I18nTranslator,
  ) {
    this.region = {
      anchor: this.anchorCell,
      height: providerProperties.length + 1,
      width: 3,
      isMerged: false,
    };
    this.disabled = disabled;
    this.gridSubRowSpan = gridSubRowSpan;
  }

  private getOptions(
    x: number,
    rowData: IntegrationPropertiesFormSchema[0],
  ): { id: string; value: string }[] {
    if (x === COLUMN_KEYS.PROVIDER) {
      return this.integrationProviderOptions.map(({ integrationProvider }) => ({
        id: integrationProvider ?? '',
        value:
          ProviderMap[integrationProvider as keyof typeof ProviderMap] ?? '',
      }));
    }

    const providerInputValue = rowData?.provider;
    if (!providerInputValue) return [];

    const providerOptions = this.integrationProviderOptions.find(
      ({ integrationProvider }) => integrationProvider === providerInputValue,
    );

    if (x === COLUMN_KEYS.PROPERTY) {
      return Object.keys(providerOptions?.integrationProperties ?? {}).map(
        (property) => ({
          id: property,
          value: PropertyMap[property as keyof typeof PropertyMap] ?? '',
        }),
      );
    }

    const propertyInputValue = rowData?.property;
    if (!propertyInputValue) return [];

    const valuesForProperty =
      providerOptions?.integrationProperties?.[
        propertyInputValue as keyof typeof providerOptions.integrationProperties
      ] ?? [];
    return valuesForProperty.map((value) => ({ id: value, value }));
  }

  private getNoOptionsText(rowData: IntegrationPropertiesFormSchema[0]) {
    if (!rowData?.provider) return this.t('selectAProvider');
    if (!rowData?.property) return this.t('selectAProperty');
    return this.t('noOptions');
  }

  private getValue(x: number, rawValue: string | undefined): string {
    if (x === COLUMN_KEYS.PROVIDER)
      return ProviderMap[rawValue as keyof typeof ProviderMap] ?? '';
    if (x === COLUMN_KEYS.PROPERTY)
      return PropertyMap[rawValue as keyof typeof PropertyMap] ?? '';
    return rawValue ?? '';
  }

  public renderContent({ cell, editing, onBlur }: SlotRenderContentProps) {
    const relative = relativeCell(cell, this.region);
    const style = {
      gridColumnStart: relative.x * this.gridSubRowSpan + 1 + this.anchorCell.x,
      gridRowStart: cell.y + 1,
      gridColumnEnd: `span ${this.gridSubRowSpan}`,
      paddingLeft: '8px',
    };

    if (relative.y === 0) {
      return {
        content: HEADER_NAMES(this.t)[
          relative.x as keyof ReturnType<typeof HEADER_NAMES>
        ],
        style,
        className:
          'bg-gray-100 text-slate-600 text-[11px] font-medium flex items-center',
        overrideGridStyles: true,
      };
    }

    const rowData = this.providerProperties[relative.y - 1] ?? {
      value: '',
      provider: '',
      property: '',
    };
    const rawValue =
      rowData?.[KEY_NAMES[relative.x as keyof typeof KEY_NAMES]] ?? '';
    // provider, property, and value fields are always strings
    const rawValueStr = String(rawValue);
    const displayValue = this.getValue(relative.x, rawValueStr);
    const options = this.getOptions(relative.x, rowData);
    const noOptionsText = this.getNoOptionsText(rowData);

    return {
      content: (
        <IntegrationCellContent
          getOptionDisabled={(option) =>
            this.getOptionDisabled(relative.x, option, rowData)
          }
          noOptionsText={noOptionsText}
          editing={editing}
          editingValue={rawValueStr}
          displayValue={displayValue}
          options={options}
          onBlur={onBlur}
          showClearButton={relative.x === COLUMN_KEYS.VALUE}
          onClear={() => this.handleClear(relative.y)}
          onChange={(value) =>
            this.handleOnChange(relative.x, relative.y, value)
          }
        />
      ),
      style,
      overrideGridStyles: true,
    };
  }

  private getOptionDisabled = (
    x: number,
    option: { id: string; value: string },
    rowData: IntegrationPropertiesFormSchema[0],
  ): boolean => {
    if (x === COLUMN_KEYS.PROVIDER) {
      const usedPropertiesByProvider = this.providerProperties.reduce(
        (acc, curr) => {
          acc[curr.provider] = [...(acc[curr.provider] || []), curr.property];
          return acc;
        },
        {} as Record<string, string[]>,
      );

      const providerProperties = Object.keys(
        this.integrationProviderOptions.find(
          (opt) => opt.integrationProvider === option.id,
        )?.integrationProperties ?? {},
      );

      const usedProperties = usedPropertiesByProvider[option.id] ?? [];
      return providerProperties.length <= usedProperties.length;
    }

    if (x === COLUMN_KEYS.PROPERTY) {
      return this.providerProperties
        .filter((prop) => prop.provider === rowData.provider)
        .some((prop) => prop.property === option.id);
    }

    return false;
  };

  public getEditBehavior(cell: Cell): CellEditBehavior {
    return relativeCell(cell, this.region).y === 0
      ? { type: 'skip' }
      : { type: 'input', onStart: () => {}, onCommit: () => {} };
  }

  private handleClear(y: number) {
    const updatedProperties = [
      ...this.providerProperties.slice(0, y - 1),
      ...this.providerProperties.slice(y),
    ];
    this.onChange(updatedProperties);
  }

  private handleOnChange(x: number, y: number, value: string) {
    const updatedProperties = [...this.providerProperties];
    const existingRow = updatedProperties[y - 1] ?? {
      provider: '',
      property: '',
      value: '',
    };

    // if a previous column changes we should clear the value of the current column
    // because their values are dependent on the previous columns
    if (x === COLUMN_KEYS.PROVIDER) {
      updatedProperties[y - 1] = {
        provider: value as Provider,
        property: '',
        value: '',
      };
    } else if (x === COLUMN_KEYS.PROPERTY) {
      updatedProperties[y - 1] = {
        provider: existingRow.provider,
        property: value as string,
        value: '',
      };
    } else if (x === COLUMN_KEYS.VALUE) {
      updatedProperties[y - 1] = {
        ...existingRow,
        value: value,
      };
    }

    this.onChange(updatedProperties);
  }
}

export function IntegrationCellContent({
  editing,
  editingValue,
  displayValue,
  options,
  showClearButton,
  noOptionsText,
  onChange,
  onClear,
  onBlur,
  getOptionDisabled,
}: {
  editing: boolean;
  editingValue: string;
  displayValue: string;
  onChange: (value: string) => void;
  options: { id: string; value: string }[];
  showClearButton: boolean;
  noOptionsText?: string;
  onClear: () => void;
  onBlur: () => void;
  getOptionDisabled: (option: { id: string; value: string }) => boolean;
}) {
  if (editing) {
    return (
      <CellInputWithDropdown
        getOptionDisabled={getOptionDisabled}
        noOptionsText={noOptionsText}
        options={options}
        value={editingValue}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  }

  return (
    <div className="flex text-xs flex-row justify-between items-center w-full">
      <span className="truncate">{displayValue}</span>
      {showClearButton && (
        <Button variant="text" size="small" onClick={onClear}>
          <MdClose size={12} className="mr-1 flex-shrink-0" />
        </Button>
      )}
    </div>
  );
}
